/**
 * Banka e orëve shtesë — LEDGER, jo një numër (PROMPT 13).
 *
 * Bilanci nuk ruhet askund: është shuma e transaksioneve të kontabilizuara
 * (CREDIT − DEBIT). Prandaj ekrani ndan qartë dy gjëra që dikur dukeshin një:
 *
 *   · «Në pritje» — orë të zbuluara nga prezenca ose të kërkuara, që ende NUK
 *     janë para në bankë. Zbulimi nuk krediton asgjë vetvetiu; dikush duhet ta
 *     dërgojë, dikush tjetër ta aprovojë, dhe vetëm pastaj kontabilizohet.
 *   · «Disponueshëm» — ajo që vërtet mund të shpenzohet sot.
 *
 * Rrjedha shfaqet si veprim: Dërgo → Aprovo → Kredito, me kthim mbrapsht si
 * transaksion i kundërt (asgjë nuk fshihet). Gjithçka në MINUTA.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Avatar, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Alert, Divider,
} from '@mui/material';
import SavingsIcon       from '@mui/icons-material/Savings';
import AddCircleIcon     from '@mui/icons-material/AddCircle';
import RemoveCircleIcon  from '@mui/icons-material/RemoveCircle';
import AccessTimeIcon    from '@mui/icons-material/AccessTime';
import HourglassTopIcon  from '@mui/icons-material/HourglassTop';
import UndoIcon          from '@mui/icons-material/Undo';
import { useTranslation } from 'react-i18next';
import usePermissions    from '../../hooks/usePermissions';
import { authFetch }     from '../../services/apiServices';

/** Minutat si h:mm — pa orë dhjetore, që asnjë minutë të mos zhduket në rrumbullakim. */
export function fmtHHMM(mins) {
  const n = Math.round(Number(mins) || 0);
  const m = Math.abs(n);
  return `${n < 0 ? '-' : ''}${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export const SOURCE_LABEL = {
  ATTENDANCE_DETECTED: 'Nga prezenca', OT_REQUEST: 'Kërkesë e aprovuar', MANUAL: 'Manual',
  REDEEM: 'Shpenzim', CARRY_IN: 'Bartje nga viti', CORRECTION: 'Korrigjim', REVERSAL: 'Kthim mbrapsht', LEGACY_LEDGER: 'Historik',
  SCHEDULE_ACCRUAL: 'Nga orari (akumuluar)',
};
export const STATUS_LABEL = { DETECTED: 'Zbuluar', PENDING: 'Në pritje', APPROVED: 'Aprovuar', CREDITED: 'Kontabilizuar', REJECTED: 'Refuzuar', VOID: 'Anuluar' };
export const STATUS_COLOR = { DETECTED: 'default', PENDING: 'warning', APPROVED: 'info', CREDITED: 'success', REJECTED: 'error', VOID: 'default' };
/** Veprimi i radhës sipas statusit — e njëjta rrjedhë si te serveri. */
export const NEXT_ACTION = { DETECTED: 'submit', PENDING: 'approve', APPROVED: 'credit' };
const ACTION_LABEL = { submit: 'Dërgo për shqyrtim', approve: 'Aprovo', credit: 'Kredito', reject: 'Refuzo' };

export default function OvertimeBankPanel({ workerId, workerName, year }) {
  const { t } = useTranslation();
  const { hasAnyRole } = usePermissions();
  const isAdmin = hasAnyRole(['super_admin', 'company_admin', 'hr_manager', 'wfm', 'team_lead', 'manager']);

  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [redeemDlg, setRedeemDlg] = useState(false);
  const [redeemMins, setRedeemMins] = useState('');
  const [redeemNote, setRedeemNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!workerId) return;
    setLoading(true); setError('');
    try {
      const r = await authFetch(`/api/overtime/balance?worker_id=${workerId}${year ? `&year=${year}` : ''}`);
      const j = await r.json();
      if (!r.ok || !j?.success) throw new Error(j?.error || `Gabim ${r.status}`);
      setBank(j.data);
    } catch (e) { setError(e.message || t('common.errorLoading', 'Gabim gjatë ngarkimit')); }
    finally { setLoading(false); }
  }, [workerId, year, t]);
  useEffect(() => { load(); }, [load]);

  const act = async (tx, action) => {
    setBusyId(tx.id); setError('');
    try {
      const r = await authFetch(`/api/overtime/transactions/${tx.id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.success) throw new Error(j?.error || `Gabim ${r.status}`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };
  const reverse = async (tx) => {
    if (!window.confirm(`Kthe mbrapsht ${fmtHHMM(tx.minutes)}? Krijohet një transaksion i kundërt — historiku nuk fshihet.`)) return;
    setBusyId(tx.id); setError('');
    try {
      const r = await authFetch(`/api/overtime/transactions/${tx.id}/reverse`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.success) throw new Error(j?.error || `Gabim ${r.status}`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const handleRedeem = async () => {
    const mins = parseInt(redeemMins, 10);
    if (!mins || mins <= 0) return;
    setSaving(true); setError('');
    try {
      // Çelës idempotence: dy klikime nuk shpenzojnë dy herë.
      const key = `redeem:${workerId}:${Date.now()}:${mins}`;
      const r = await authFetch('/api/overtime/redeem', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ worker_id: workerId, minutes: mins, note: redeemNote || undefined, idempotency_key: key }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.success) throw new Error(j?.error || `Gabim ${r.status}`);
      setRedeemDlg(false); setRedeemMins(''); setRedeemNote('');
      await load();
    } catch (e) { setError(e.message || t('common.errorSaving', 'Gabim gjatë ruajtjes')); }
    finally { setSaving(false); }
  };

  const open = useMemo(() => (bank?.Open || []), [bank]);
  const posted = useMemo(() => (bank?.Transactions || []).filter((x) => x.status === 'CREDITED'), [bank]);
  if (!workerId) return null;
  const available = bank?.BalanceMinutes ?? 0;
  const pending = bank?.PendingCreditMinutes ?? 0;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SavingsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>{t('overtimeBank.title', 'Banka e Orëve Jashtë Orarit')}</Typography>
          {workerName && <Chip size="small" label={workerName} sx={{ fontSize: '0.72rem' }} />}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {year && <Chip size="small" label={year} variant="outlined" />}
          {isAdmin && (
            <Button size="small" variant="outlined" color="warning" startIcon={<RemoveCircleIcon />}
              onClick={() => setRedeemDlg(true)} disabled={available <= 0 || loading}
              sx={{ textTransform: 'none', fontSize: '0.78rem' }} data-testid="ot-redeem">
              {t('overtimeBank.redeem', 'Shpenzo Orë')}
            </Button>
          )}
        </Stack>
      </Stack>

      {loading ? <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        : error ? <Box sx={{ p: 2 }}><Alert severity="error" onClose={() => setError('')}>{error}</Alert></Box>
        : (
        <>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{ px: 2, py: 2 }}>
            {[
              { label: 'Kredituar', val: bank?.AccruedMinutes || 0, color: 'success.main', icon: <AddCircleIcon fontSize="small" /> },
              { label: 'Shpenzuar', val: bank?.UsedMinutes || 0, color: 'warning.main', icon: <RemoveCircleIcon fontSize="small" /> },
              { label: 'Në pritje', val: pending, color: 'text.secondary', icon: <HourglassTopIcon fontSize="small" />, hint: 'Zbuluar ose në shqyrtim — ende jashtë bilancit' },
              { label: 'Disponueshëm', val: available, color: available > 0 ? 'primary.main' : 'text.disabled', icon: <SavingsIcon fontSize="small" />, bold: true, hint: 'Kredite − debite, vetëm transaksionet e kontabilizuara' },
            ].map((c) => (
              <Box key={c.label} sx={{ flex: 1, textAlign: 'center', px: 1 }}>
                <Tooltip title={c.hint || ''}>
                  <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ color: c.color, mb: 0.25 }}>
                    {c.icon}
                    <Typography variant="h6" fontWeight={c.bold ? 800 : 600} color={c.color}>{fmtHHMM(c.val)}</Typography>
                  </Stack>
                </Tooltip>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
              </Box>
            ))}
          </Stack>

          {/* Orët e akumuluara shpenzohen si ditë pushimi: një ditë = orari i punëtorit. */}
          {bank?.LeaveDayConversion?.available && (
            <Box sx={{ mx: 2, mb: 2, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.3)',
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid="ot-days">
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#92400E' }}>
                ≈ {Number(bank.LeaveDayConversion.days).toLocaleString('sq-AL')} ditë pushimi
              </Typography>
              <Typography variant="caption" color="text.secondary">
                disponueshme · 1 ditë = {fmtHHMM(bank.LeaveDayConversion.dayMinutes)}
                {bank.LeaveDayConversion.basis === 'schedule' ? ' (sipas orarit të punëtorit)' : ' (sipas cilësimit të kompanisë)'}
              </Typography>
            </Box>
          )}

          {open.length > 0 && (
            <>
              <Divider />
              <Box sx={{ px: 2, py: 1.25, bgcolor: 'warning.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'warning.dark' }}>
                  Në rrjedhë · {open.length}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Orët e zbuluara nuk kreditohen vetvetiu: Dërgo → Aprovo → Kredito.
                </Typography>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableBody>
                    {open.map((x) => (
                      <TableRow key={x.id} hover data-testid="ot-open-row">
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', width: 100 }}>{x.occurredAt}</TableCell>
                        <TableCell sx={{ width: 130 }}><Chip size="small" color={STATUS_COLOR[x.status]} label={STATUS_LABEL[x.status] || x.status} sx={{ fontSize: '0.7rem' }} /></TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{fmtHHMM(x.minutes)}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{SOURCE_LABEL[x.source] || x.source}{x.note ? ` · ${x.note}` : ''}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {NEXT_ACTION[x.status] && (
                            <Button size="small" variant="contained" disableElevation disabled={busyId === x.id}
                              onClick={() => act(x, NEXT_ACTION[x.status])} sx={{ textTransform: 'none', mr: 0.5, fontSize: '0.75rem' }}>
                              {ACTION_LABEL[NEXT_ACTION[x.status]]}
                            </Button>
                          )}
                          {isAdmin && (
                            <Button size="small" color="error" disabled={busyId === x.id} onClick={() => act(x, 'reject')} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                              {ACTION_LABEL.reject}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}

          <Divider />
          <Box sx={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Data', 'Burimi', 'Minuta', 'Shënim', 'Nga', ''].map((h, i) => (
                    <TableCell key={h || i} sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {posted.map((x, i) => (
                  <TableRow key={x.id} hover sx={{ bgcolor: i % 2 ? '#FAFAFA' : 'white' }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{x.occurredAt}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={SOURCE_LABEL[x.source] || x.source} sx={{ fontSize: '0.7rem' }} /></TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: x.direction === 'CREDIT' ? 'success.main' : 'error.main' }}>
                      {x.direction === 'CREDIT' ? '+' : '−'}{fmtHHMM(x.minutes)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', maxWidth: 220 }}>{x.note || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {x.creditedBy && <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: 'primary.light' }}>{(x.creditedBy[0] || '').toUpperCase()}</Avatar>}
                        <span>{x.creditedBy || x.approvedBy || x.createdBy || '—'}</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {isAdmin && x.source !== 'REVERSAL' && (
                        <Tooltip title="Kthe mbrapsht me transaksion të kundërt">
                          <span><Button size="small" disabled={busyId === x.id} onClick={() => reverse(x)} startIcon={<UndoIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>Kthe</Button></span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {posted.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                    {t('overtimeBank.noLedger', 'Asnjë transaksion i kontabilizuar akoma.')}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              <AccessTimeIcon sx={{ fontSize: 12, mr: .5, verticalAlign: 'middle' }} />
              Njësia është minuta. Konvertimi në ditë pushimi nuk aplikohet — formula pret aprovimin e klientit.
            </Typography>
          </Box>
        </>
      )}

      <Dialog open={redeemDlg} onClose={() => setRedeemDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('overtimeBank.redeemTitle', 'Shpenzo Orë nga Banka')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Disponueshëm: <strong>{fmtHHMM(available)}</strong>
            </Typography>
            <TextField type="number" label="Minuta për të shpenzuar" value={redeemMins}
              onChange={(e) => setRedeemMins(e.target.value)} inputProps={{ min: 1, max: available }} size="small" fullWidth autoFocus />
            <TextField label="Arsyeja / Shënim" value={redeemNote} onChange={(e) => setRedeemNote(e.target.value)} size="small" fullWidth multiline rows={2} />
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedeemDlg(false)} disabled={saving}>{t('common.cancel', 'Anulo')}</Button>
          <Button variant="contained" color="warning" disabled={saving || !redeemMins} onClick={handleRedeem}
            startIcon={saving ? <CircularProgress size={14} /> : <RemoveCircleIcon />}>Shpenzo</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
